import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import AuditService from "services/Audit";
import CampaignService from "services/Campaign";
import { getUsersForFiltersQuery } from "helpers/user";

// Campaign, user, activity date and the manual toggle are not event types:
// they narrow whichever events matched. The API reports only the event groups,
// so these are appended here.
const CAMPAIGN_FILTER = {
  key: "campaign_id",
  title: "Campaign",
  type: "ruvixxSelect",
  queryFn: CampaignService.getCampaignsForFilters,
};

const USER_FILTER = {
  key: "user_id",
  title: "User",
  type: "ruvixxSelect",
  queryFn: getUsersForFiltersQuery,
};

const ACTIVITY_DATE_FILTER = {
  key: "created_at",
  title: "Activity Date",
  type: "dateRange",
};

const IS_MANUAL_FILTER = {
  key: "is_manual",
  title: "Is manual",
  type: "ruvixxToggle",
};

// A case's, contact's or entity's campaign membership and status are options
// of its buckets, so a campaign scope there would duplicate them. A campaign's
// own feed is already scoped to it.
const WITHOUT_CAMPAIGN_FILTER = ["Campaign", "Case", "Contact", "Entity"];

// Each API group becomes one select descriptor. One descriptor per group is
// how the panel expresses grouping: its multi-select is a flat searchable
// combobox with no option headings of its own.
const groupToDescriptor = group => ({
  key: group.key,
  title: group.title,
  type: "select",
  data: group.options,
});

/**
 * Advanced-search filter descriptors for a record's Activity Log.
 *
 * The event groups come from the API so a route is only offered filters its
 * audit trail can actually produce.
 */
export function useAuditLogFilters({ modelType, slug }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["audit-log-filter-options", modelType, slug],
    queryFn: () => AuditService.getAuditLogFilterOptions({ modelType, slug }),
    enabled: !!modelType,
  });

  const filters = useMemo(() => {
    const groups = data?.groups ?? [];
    if (!groups.length) return [];
    return [
      ...groups.map(groupToDescriptor),
      ...(WITHOUT_CAMPAIGN_FILTER.includes(modelType) ? [] : [CAMPAIGN_FILTER]),
      USER_FILTER,
      ACTIVITY_DATE_FILTER,
      IS_MANUAL_FILTER,
    ];
  }, [data, modelType]);

  return { filters, isLoading, isError };
}
